import { useCallback, useEffect, useState, useRef } from 'react';
import '../../Styles/index.css';
import izenuAPI from '../../services/izenuAPI';
import openArtistInApp from './openArtistInApp';
import EditUserModal from './EditUserModal';
import CreateUserModal from './CreateUserModal';

const CDN_URL = process.env.REACT_APP_CDN_URL || 'https://dpagz47n4g1lw.cloudfront.net';


// A podcaster is acc_type 'creator' (see EditUserModal).
const ACC_TYPE_LABELS = { listener: 'Ouvinte', artist: 'Artista', creator: 'Podcaster' };

export default function Users() {
    const [users, setUsers] = useState([]);
    const [distributors, setDistributors] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [accTypeFilter, setAccTypeFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const searchRef = useRef(null);

    // Edit modal state
    const [editUser, setEditUser] = useState(null);
    const [creating, setCreating] = useState(false);

    // Convert modal state
    const [convertUser, setConvertUser] = useState(null);
    const [convertArtistName, setConvertArtistName] = useState('');
    const [convertDistributorId, setConvertDistributorId] = useState('1');
    const [converting, setConverting] = useState(false);

    const fetchUsers = useCallback(async (page, search, accType) => {
        setLoading(true);
        try {
            let url = `/admin/user/listUsers.php?page=${page}&limit=50`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (accType) url += `&acc_type=${accType}`;

            const { data: res } = await izenuAPI.get(url);
            if (res.response) {
                setUsers(res.users);
                setTotal(res.total);
                setTotalPages(res.pages);
                if (res.distributors) setDistributors(res.distributors);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers(currentPage, searchTerm, accTypeFilter);
        }, searchTerm ? 400 : 0);
        return () => clearTimeout(timer);
    }, [currentPage, searchTerm, accTypeFilter, fetchUsers]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleAccTypeFilter = (e) => {
        setAccTypeFilter(e.target.value);
        setCurrentPage(1);
    };

    // --- Edit Modal ---
    const openEditModal = (user) => setEditUser(user);

    // --- Impersonate Artist ---
    const impersonateArtist = useCallback(async (user) => {
        if (!window.confirm(`Entrar no painel de "${user.artist_name || user.name}"?`)) return;
        try {
            const { data: res } = await izenuAPI.post('/admin/artist/impersonate.php', {
                user_id: user.user_id
            });
            if (!res.response) {
                alert(res.message || 'Erro ao impersonar artista');
                return;
            }
            localStorage.setItem('admin_backup_token', localStorage.getItem('token'));
            localStorage.setItem('admin_backup_type', localStorage.getItem('type'));
            localStorage.setItem('admin_backup_id', localStorage.getItem('id'));
            localStorage.setItem('admin_backup_name', localStorage.getItem('name'));
            localStorage.setItem('admin_backup_user', localStorage.getItem('user'));
            localStorage.setItem('token', res.token);
            localStorage.setItem('type', 'artist');
            localStorage.setItem('id', res.artist.user_id);
            localStorage.setItem('name', res.artist.name);
            localStorage.setItem('user', JSON.stringify(res.artist));
            localStorage.setItem('impersonating', 'true');
            localStorage.setItem('impersonating_name', res.artist.artist_name || res.artist.name);
            window.location.href = '/';
        } catch (error) {
            console.log(error);
            alert('Erro ao impersonar artista');
        }
    }, []);

    // --- Convert to Artist ---
    const openConvertModal = (user) => {
        setConvertUser(user);
        setConvertArtistName(user.name || '');
        setConvertDistributorId('1');
    };

    const closeConvertModal = () => {
        setConvertUser(null);
        setConvertArtistName('');
        setConvertDistributorId('1');
    };

    const doConvert = async () => {
        if (!convertUser) return;
        setConverting(true);
        try {
            const { data: res } = await izenuAPI.post('/admin/user/convertToArtist.php', {
                user_id: convertUser.user_id,
                artist_name: convertArtistName,
                distributor_id: parseInt(convertDistributorId)
            });
            if (res.response) {
                closeConvertModal();
                fetchUsers(currentPage, searchTerm, accTypeFilter);
            } else {
                alert(res.message || 'Erro ao converter');
            }
        } catch (error) {
            console.log(error);
            alert('Erro ao converter utilizador');
        }
        setConverting(false);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const accTypeBadge = (type) => {
        const styles = {
            artist: { background: '#ecfdf5', color: '#059669' },
            listener: { background: '#f0f9ff', color: '#0284c7' },
            creator: { background: '#f5f3ff', color: '#7c3aed' },
            distributor: { background: '#fef3c7', color: '#d97706' },
        };
        const s = styles[type] || styles.listener;
        return (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 12, ...s }}>
                {ACC_TYPE_LABELS[type || 'listener'] || type}
            </span>
        );
    };

    const verifiedBadge = (v) => {
        if (v === 'yes') {
            return <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 12, background: '#ecfdf5', color: '#059669' }}>Verificado</span>;
        }
        return null;
    };

    return (
        <div className="p-10">
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <div className="flex flex-wrap items-center justify-between gap-3" style={{ marginBottom: 8 }}>
                    <h2 style={{ fontSize: 24, fontWeight: 700, color: '#333' }}>
                        Utilizadores
                    </h2>
                    <button type="button" onClick={() => setCreating(true)}
                        style={{ padding: '8px 16px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer' }}>
                        + Novo utilizador
                    </button>
                </div>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 12 }}>
                    Todos os utilizadores da plataforma. Pesquise, edite dados e converta ouvintes em artistas.
                </p>
                <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
                    {total.toLocaleString('pt-PT')} {total === 1 ? 'utilizador' : 'utilizadores'}
                </span>
            </div>

            {/* Filters */}
            <div className="flex gap-4 mb-4 items-end flex-wrap">
                <div style={{ maxWidth: 400, flex: 1 }}>
                    <div className="search-distributors-input">
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Pesquisar por nome, nome artístico ou email..."
                            value={searchTerm}
                            onChange={handleSearch}
                            style={{ padding: '6px 0', fontSize: 14 }}
                        />
                    </div>
                </div>
                <div>
                    <select
                        value={accTypeFilter}
                        onChange={handleAccTypeFilter}
                        style={{
                            padding: '8px 12px', fontSize: 14, borderRadius: 8,
                            border: '1px solid #d1d5db', color: '#374151',
                            background: '#fff', cursor: 'pointer', minWidth: 180
                        }}
                    >
                        <option value="">Todos os tipos</option>
                        <option value="listener">Ouvinte</option>
                        <option value="artist">Artista</option>
                        <option value="creator">Podcaster</option>
                    </select>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin fill-amber-400" viewBox="0 0 100 101" fill="none">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                    <p className="text-amber-500 font-light text-lg mt-3">A carregar...</p>
                </div>
            ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <p className="text-gray-500 text-lg">Nenhum utilizador encontrado</p>
                </div>
            ) : (
                <>
                    <div className="border rounded-lg max-h-[600px] overflow-y-auto p-5">
                        {users.map((e) => {
                            const photo = e.photo ? `${CDN_URL}/users/${e.user_id}/profile_photos/${e.photo}` : null;

                            return (
                                <div key={e.user_id} className="border p-3 flex gap-4 flex-row mb-3 items-center rounded-lg hover:bg-gray-50 transition">
                                    <div className="rounded-full bg-gray-300 h-12 w-12 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                        {photo ? (
                                            <img className="h-12 w-12 object-cover rounded-full" src={photo} alt="" />
                                        ) : (
                                            <svg className="text-gray-500 h-12 w-12" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z" />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="p-0 m-0 text-left font-semibold text-gray-900">
                                                {e.artist_name || e.name}
                                            </h3>
                                            {accTypeBadge(e.acc_type)}
                                            {verifiedBadge(e.verified)}
                                            {e.distributor_name && (
                                                <span style={{
                                                    fontSize: 11, fontWeight: 600,
                                                    background: '#eef2ff', color: '#4f46e5',
                                                    padding: '1px 8px', borderRadius: 12
                                                }}>
                                                    {e.distributor_name}
                                                </span>
                                            )}
                                            {!e.distributor_name && (e.acc_type === 'artist' || e.acc_type === 'creator') && (
                                                <span title="Não aparece no painel de nenhuma distribuidora" style={{
                                                    fontSize: 11, fontWeight: 600,
                                                    background: '#fef2f2', color: '#dc2626',
                                                    padding: '1px 8px', borderRadius: 12
                                                }}>
                                                    Sem distribuidora
                                                </span>
                                            )}
                                        </div>
                                        {e.artist_name && e.name !== e.artist_name && (
                                            <p className="p-0 m-0 text-left text-xs text-gray-500">{e.name}</p>
                                        )}
                                        <p className="p-0 m-0 text-left text-sm text-gray-600">{e.email}</p>
                                        <p className="p-0 m-0 text-left text-xs text-gray-400">
                                            ID: {e.user_id} {e.phone ? ` · ${e.phone}` : ''} · {formatDate(e.created_at)}
                                        </p>
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                        {/* Impersonate button (only for artists) */}
                                        {e.acc_type === 'artist' && (
                                            <button onClick={() => impersonateArtist(e)} className="p-2 text-gray-500 hover:text-amber-500 rounded-lg hover:bg-gray-100" title="Ver como artista">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            </button>
                                        )}
                                        {e.acc_type === 'artist' && (
                                            <button onClick={() => openArtistInApp(e)} className="p-2 text-gray-500 hover:text-rose-500 rounded-lg hover:bg-gray-100" title="Abrir no app.izenu.ao como artista" aria-label="Abrir no app.izenu.ao como artista">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </button>
                                        )}
                                        {/* Edit button */}
                                        <button onClick={() => openEditModal(e)} className="p-2 text-gray-500 hover:text-indigo-500 rounded-lg hover:bg-gray-100" title="Editar utilizador">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        {/* Convert to artist button (only for non-artists) */}
                                        {e.acc_type !== 'artist' && (
                                            <button onClick={() => openConvertModal(e)} className="p-2 text-gray-500 hover:text-green-600 rounded-lg hover:bg-gray-100" title="Converter em artista">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 mt-4">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage <= 1}
                                className={`text-sm font-medium px-3 py-1.5 rounded border transition ${
                                    currentPage <= 1
                                        ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-600 border-gray-300 hover:border-amber-400 hover:text-amber-600'
                                }`}
                            >
                                ← Anterior
                            </button>
                            <span className="text-sm text-gray-500">
                                Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                                className={`text-sm font-medium px-3 py-1.5 rounded border transition ${
                                    currentPage >= totalPages
                                        ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-600 border-gray-300 hover:border-amber-400 hover:text-amber-600'
                                }`}
                            >
                                Próxima →
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Edit Modal */}
            {editUser && (
                <EditUserModal
                    user={editUser}
                    distributors={distributors}
                    onClose={() => setEditUser(null)}
                    onSaved={() => { setEditUser(null); fetchUsers(currentPage, searchTerm, accTypeFilter); }}
                />
            )}

            {/* Create Modal */}
            {creating && (
                <CreateUserModal
                    distributors={distributors}
                    onClose={() => setCreating(false)}
                    onCreated={() => {
                        setCreating(false);
                        // The new account is the newest, so it shows first on page 1 without filters.
                        setSearchTerm('');
                        setAccTypeFilter('');
                        setCurrentPage(1);
                        fetchUsers(1, '', '');
                    }}
                />
            )}

            {/* Convert to Artist Modal */}
            {convertUser && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div onClick={closeConvertModal} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
                    <div style={{ position: 'relative', background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420 }}>
                        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: '#333' }}>
                            Converter em Artista
                        </h3>
                        <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                            O utilizador <strong>{convertUser.name}</strong> (ID: {convertUser.user_id}) será convertido em artista e verificado.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <label style={labelStyle}>
                                Nome artístico
                                <input style={inputStyle} value={convertArtistName} onChange={e => setConvertArtistName(e.target.value)} placeholder={convertUser.name} />
                            </label>
                            <label style={labelStyle}>
                                Distribuidora
                                <select style={inputStyle} value={convertDistributorId} onChange={e => setConvertDistributorId(e.target.value)}>
                                    {distributors.map(d => (
                                        <option key={d.distributor_id} value={d.distributor_id}>{d.name}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button onClick={closeConvertModal} style={btnSecondary}>Cancelar</button>
                            <button onClick={doConvert} disabled={converting} style={{ ...btnPrimary, background: '#059669' }}>
                                {converting ? 'A converter...' : 'Converter'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#374151' };
const inputStyle = { padding: '8px 12px', fontSize: 14, borderRadius: 8, border: '1px solid #d1d5db', color: '#374151', background: '#fff', width: '100%' };
const btnPrimary = { padding: '8px 20px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer' };
const btnSecondary = { padding: '8px 20px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' };
